import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { AlertTriangle, ArrowRight, Search, Users } from 'lucide-react'
import {
  currentSession,
  fetchDancerDay,
  minutesNow,
  searchDancers,
  standing,
  type DancerSession,
} from '../api/dancers'
import { formatFullDate, formatTime, meridiem, today } from '../api/calendar'
import CalendarDatePicker from './CalendarDatePicker'
import BackLink from './BackLink'

function TimeSpan({ session }: { session: DancerSession }) {
  return (
    <span className="tabular">
      {formatTime(session.startTime)}
      {meridiem(session.startTime)}&ndash;{formatTime(session.endTime)}
      {meridiem(session.endTime)}
    </span>
  )
}

/**
 * "Where does my dancer need to be?"
 *
 * Built for the counter: a parent who does not usually do drop-off arrives and
 * asks. Front desk types part of a name and gets the answer in one glance,
 * while the parent is standing there — so the page leads with what is happening
 * *now* in large type, and only then shows the rest of the evening.
 *
 * Household-aware, because the same screen is what a parent sees of their own
 * dancers, and a parent with three children wants all three rather than three
 * separate lookups.
 */
export default function WhereIsView() {
  const [params, setParams] = useSearchParams()
  const [q, setQ] = useState('')

  const selected = params.get('dancer')
  const date = params.get('date') ?? today()
  const isToday = date === today()

  const { data: hits, isFetching } = useQuery({
    queryKey: ['dancer-search', q],
    queryFn: () => searchDancers(q),
    enabled: q.trim().length >= 2,
  })

  const { data: day } = useQuery({
    queryKey: ['dancer-day', selected, date],
    queryFn: () => fetchDancerDay(selected!, date),
    enabled: Boolean(selected),
  })

  const now = minutesNow()
  const lead = day ? currentSession(day.sessions, now, isToday) : null
  const leading = lead ? standing(lead, now, isToday) : null

  const select = (dancerId: string) => {
    const next = new URLSearchParams(params)
    next.set('dancer', dancerId)
    setParams(next, { replace: true })
  }

  return (
    <div className="page-shell px-5 py-8">
      {/* Reached from a server-built search href, from any screen at all, so
          the fallback has to be somewhere everyone can go (D-353). */}
      <BackLink to="/" label="Home" />

      <header className="mb-5">
        <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Find a dancer</p>
        <CalendarDatePicker
          label={formatFullDate(date)}
          anchor={date}
          view="day"
          onPick={(d) => {
            const next = new URLSearchParams(params)
            next.set('date', d)
            setParams(next, { replace: true })
          }}
        />
      </header>

      <div className="relative mb-4">
        <Search
          size={15}
          strokeWidth={2.5}
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-3"
        />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Type part of a dancer's name"
          aria-label="Search for a dancer"
          autoFocus
          className="w-full rounded-xl border border-line-2 bg-surface py-2.5 pr-3 pl-9 text-[14px] outline-none placeholder:text-ink-3 focus:border-accent"
        />
      </div>

      {q.trim().length >= 2 && (
        <div className="mb-5 overflow-hidden rounded-xl border border-line bg-surface">
          {isFetching && !hits && <p className="px-4 py-3 text-[13px] text-ink-3">Searching...</p>}

          {hits && hits.length === 0 && (
            <p className="px-4 py-3 text-[13px] text-ink-3">No dancer matches that name.</p>
          )}

          {hits?.map((hit) => (
            <button
              key={hit.dancerId}
              onClick={() => select(hit.dancerId)}
              className={`flex w-full items-baseline gap-2 border-b border-line px-4 py-2.5 text-left transition-colors last:border-b-0 hover:bg-surface-2 ${
                hit.dancerId === selected ? 'bg-accent-soft' : ''
              }`}
            >
              <span className="flex-1 truncate text-[13.5px] font-semibold">{hit.name}</span>
              <span className="tabular shrink-0 text-[12px] text-ink-3">{hit.ageYears}</span>
              {hit.household && (
                <span className="w-36 shrink-0 truncate text-right text-[12px] text-ink-3">
                  {hit.household}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {day && (
        <section>
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <h1 className="font-display text-2xl">{day.name}</h1>
            {day.household && <span className="text-[12.5px] text-ink-3">{day.household}</span>}
          </div>

          {/* Siblings, so one lookup covers the whole family at pickup. */}
          {day.siblings.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-1.5">
              <Users size={13} strokeWidth={2.5} className="text-ink-3" />
              {day.siblings.map((sibling) => (
                <button
                  key={sibling.dancerId}
                  onClick={() => select(sibling.dancerId)}
                  className="rounded-full border border-line-2 bg-surface px-2.5 py-1 text-[12px] font-semibold text-ink-2 transition-colors hover:border-ink-3"
                >
                  {sibling.name}
                </button>
              ))}
            </div>
          )}

          {/*
            The answer, in the largest type on the page. Everything below is
            reference; this line is what the person at the counter is asking for.
          */}
          {lead ? (
            <div
              className="room-tint mb-4 rounded-2xl border border-line p-5"
              style={
                {
                  '--room-color': lead.roomColor ?? 'var(--color-accent)',
                  background: 'var(--room-fill)',
                  borderLeft: '5px solid var(--room-display)',
                } as React.CSSProperties
              }
            >
              <p className="text-[11px] font-bold tracking-[0.14em] uppercase" style={{ color: 'var(--room-display)' }}>
                {leading === 'now' ? 'Right now' : 'Up next'}
              </p>
              <p className="mt-1 font-display text-2xl">{lead.title}</p>
              <p className="mt-1 text-[14px] text-ink-2">
                {lead.roomName && <strong>{lead.roomName}</strong>}
                {lead.roomName && ' · '}
                <TimeSpan session={lead} />
                {lead.instructors.length > 0 && <> · {lead.instructors.join(', ')}</>}
              </p>
            </div>
          ) : (
            <p className="mb-4 rounded-2xl border border-line bg-surface-2 px-5 py-4 text-[14px] text-ink-3">
              {day.sessions.length === 0
                ? `${day.name} has nothing scheduled on this day.`
                : isToday
                  ? `${day.name} is finished for today.`
                  : 'Not today — the full day is below.'}
            </p>
          )}

          {/*
            <b>The way on to the full record</b> (D-460).

            `asked`: the owner wanted the header search to open a dancer's
            profile, and chose at the gate to reach it *through* this screen
            rather than instead of it — "where is she right now" is what
            somebody at the desk with a waiting parent needs, and the record
            cannot answer it.

            <b>Not "Open her record".</b> The studio has boys on the teams, and
            the owner has reported gendered copy here before.

            <b>A labelled control rather than the heading itself.</b> The
            roster's idiom (D-397) makes the name the door, with no underline
            and colour only on hover. That works in a list where every row is a
            door; on a lone heading there is nothing to tell it from the heading
            it was yesterday, `headings.test.ts` forbids changing its size or
            face to signal it, and **there is no hover on the front-desk touch
            screen this page is used on**. `asked` at the gate, over reusing the
            idiom.

            <b>No `useCan`.</b> `/find` and `/admin/dancers/:id` are both gated
            on `ViewPeople`, so anyone reading this can open the destination.
            That is a coincidence of two independent declarations rather than
            anything holding them together — so `routes.test.ts`, under "routes
            a link crosses between", asserts that the pair agree and fails if
            either moves alone (D-460).

            <b>Below the answer, not above it.</b> This screen's whole purpose
            is the line telling the person at the counter where the dancer is
            *now*, in the largest type on the page. Sitting between the name and
            that line pushed it about 48px down a 390px phone — the record is
            the second question, so it waits until the first is answered.

            <b>Addressed from `day.dancerId`, not from the query parameter.</b>
            Same object that gates this rendering, so the name above and the
            record it opens cannot disagree — a pivot to a sibling moves both or
            neither.
          */}
          <Link
            to={`/admin/dancers/${day.dancerId}`}
            className="mb-4 inline-flex min-h-[44px] items-center gap-1 text-[13px] font-semibold text-accent-2"
          >
            Open full record
            <ArrowRight size={14} strokeWidth={2.5} className="shrink-0" />
          </Link>

          {day.sessions.some((s) => s.clashes) && (
            <p className="mb-4 flex items-center gap-2 rounded-xl border border-crit bg-crit-bg px-4 py-2.5 text-[13px] font-semibold text-crit">
              <AlertTriangle size={15} strokeWidth={2.5} />
              {day.name} is scheduled in two places at once.
            </p>
          )}

          {day.sessions.length > 0 && (
            <ol className="flex flex-col gap-1.5">
              {day.sessions.map((session) => {
                const where = standing(session, now, isToday)
                return (
                  <li
                    key={session.id}
                    className={`room-tint flex items-baseline gap-3 rounded-lg border px-3.5 py-2.5 ${
                      session.clashes ? 'border-crit' : 'border-line'
                    } ${where === 'earlier' ? 'opacity-55' : ''}`}
                    style={
                      {
                        '--room-color': session.roomColor ?? 'var(--color-ink-3)',
                        borderLeft: '3px solid var(--room-display)',
                      } as React.CSSProperties
                    }
                  >
                    <span className="w-32 shrink-0 text-[12.5px] font-semibold text-ink-2">
                      <TimeSpan session={session} />
                    </span>
                    <span className="flex-1 truncate text-[13.5px] font-semibold">
                      {session.title}
                    </span>
                    {session.roomName && (
                      <span className="shrink-0 text-[12px] text-ink-3">{session.roomName}</span>
                    )}
                  </li>
                )
              })}
            </ol>
          )}
        </section>
      )}
    </div>
  )
}
